import React, { memo, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { HiCalendar } from "react-icons/hi";
import { DUMMY_REGEX_EXP } from "Constants/constant";
import { classNames } from "Utils/utils";
import { ADVANCE_TIME_INPUT_CLASS } from "./advanceModeUi";

/** Fixed display format — independent of OS/browser locale. */
export const SHIFT_DATE_DISPLAY_FORMAT = "dd/MM/yyyy";
export const SHIFT_DATE_VALUE_FORMAT = "YYYY-MM-DD";

/** Above fullscreen Advanced Edit modal (z-index 99999). */
export const SHIFT_DATE_PICKER_POPPER_Z_INDEX = 100020;

const parseShiftDateValue = (value) => {
  if (!value) return null;
  const strict = moment(value, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
  if (strict.isValid()) return strict.toDate();
  const loose = moment(value);
  return loose.isValid() ? loose.toDate() : null;
};

const toShiftDateValue = (date) =>
  date ? moment(date).format(SHIFT_DATE_VALUE_FORMAT) : "";

/**
 * Locale-independent shift date control.
 * Displays DD/MM/YYYY; stores YYYY-MM-DD for form/API compatibility.
 */
const ShiftDatePicker = ({
  name = "shift_date",
  value = "",
  register = null,
  setValue = null,
  pattern = null,
  isRequired = false,
  isDisabled = false,
  readOnly = false,
  ErrorMessage = null,
  placeholder = "dd/mm/yyyy",
  onDateChange = null,
}) => {
  const selectedDate = useMemo(() => parseShiftDateValue(value), [value]);
  const isLocked = Boolean(isDisabled || readOnly);

  const registerProps =
    register && typeof register === "function"
      ? register(name, {
          required: isRequired,
          // Keep the value in RHF watch()/setValue even when the picker is
          // locked. `disabled: true` (RHF 7.33) sets the field to undefined and
          // ignores later setValue — that skipped NDIS line-item fetches on
          // recurring / in-progress advance edits.
          value: value || "",
          pattern:
            pattern !== null && pattern !== false && value
              ? pattern.test(value)
                ? false
                : DUMMY_REGEX_EXP
              : false,
        })
      : null;

  const handleChange = (date) => {
    if (isLocked || !setValue) return;
    const nextDate = toShiftDateValue(date);
    setValue(name, nextDate, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    if (typeof onDateChange === "function") {
      onDateChange(nextDate);
    }
  };

  return (
    <>
      {registerProps ? (
        <input type='hidden' {...registerProps} readOnly aria-hidden />
      ) : null}
      <div className='relative shift-date-picker'>
        <DatePicker
          selected={selectedDate}
          onChange={handleChange}
          dateFormat={SHIFT_DATE_DISPLAY_FORMAT}
          placeholderText={placeholder}
          disabled={isLocked}
          popperClassName='shift-date-picker-popper'
          popperProps={{ strategy: "fixed" }}
          className={classNames(
            ADVANCE_TIME_INPUT_CLASS,
            "shift-date-picker__input w-full",
            ErrorMessage && "errorBorder"
          )}
          onKeyDown={(e) => {
            // Prevent free-text locale-dependent typing; selection via calendar only.
            if (e.key !== "Tab" && e.key !== "Escape") {
              e.preventDefault();
            }
          }}
        />
        <span
          className={classNames(
            "shift-date-picker__icon",
            isLocked && "shift-date-picker__icon--locked"
          )}
          aria-hidden
        >
          <HiCalendar className='h-4 w-4' />
        </span>
      </div>
      {ErrorMessage ? (
        <span
          title={ErrorMessage}
          className='truncate'
          style={{ fontSize: "13px", color: "red", marginLeft: "5px" }}
        >
          {ErrorMessage}
        </span>
      ) : null}
    </>
  );
};

export default memo(ShiftDatePicker);
